import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Smartphone, X, Download, Wifi, WifiOff } from 'lucide-react';
import { usePWA } from '@/hooks/use-pwa';

export const PWAInstallPrompt = () => {
  const { isInstallable, isOffline, installApp } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isInstallable && !dismissed) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, dismissed]);

  const handleInstall = async () => {
    const installed = await installApp();
    if (installed) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    // Remember dismissal for 24 hours
    localStorage.setItem('pwa-dismissed', Date.now().toString());
  };

  useEffect(() => {
    const dismissedTime = localStorage.getItem('pwa-dismissed');
    if (dismissedTime) {
      const daysPassed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysPassed < 1) {
        setDismissed(true);
      }
    }
  }, []);

  if (!showPrompt || !isInstallable || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
      <Card className="shadow-lg border-[#2a2d70]/20">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-[#2a2d70]" />
              <CardTitle className="text-lg">Install App</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>
            Install Belednai Technology app for a better experience
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2 text-sm text-gray-600 mb-4">
            <div className="flex items-center gap-2">
              <Download className="h-4 w-4 text-[#2a2d70]" />
              <span>Works offline</span>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-[#2a2d70]" />
              <span>App-like experience</span>
            </div>
            <div className="flex items-center gap-2">
              {isOffline ? (
                <WifiOff className="h-4 w-4 text-red-600" />
              ) : (
                <Wifi className="h-4 w-4 text-[#2a2d70]" />
              )}
              <span>{isOffline ? 'Currently offline' : 'Fast and reliable'}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleInstall} className="flex-1">
              Install
            </Button>
            <Button variant="outline" onClick={handleDismiss}>
              Not now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 